// To parse this data:
//
//   import { Convert, ActivitiesSchema } from "./file";
//
//   const activitiesSchema = Convert.toActivitiesSchema(json);
//
// These functions will throw an error if the JSON doesn't
// match the expected interface, even if the JSON is valid.

/**
 * Activity Schema
 */
export interface ActivitiesSchema {
    created_by:    number;
    date_created:  string;
    date_modified: string;
    id?:           number;
    modified_by:   number;
    data:          Data;
    }

export interface Data {
    areas:                     number[];
    blank_sticky_id?:          number;
    comments:                  number[];
    company:                   number;
    completed:                 boolean;
    crew_size?:                number;
    date_scanned?:             string[];
    description?:              string;
    end_date?:                 string;
    estimated_duration:        number;
    mergedActivityNumbers?:    number;
    number:                    number;
    origin?:                   Origin;
    predecessor?:              Array<number | string>;
    print_count:               number;
    start_date?:               string;
    tags:                      string[];
    title:                     string;
    track_in_master_schedule?: boolean;
    variance_reasons?:         VarianceReason[];
    }

export interface Origin {
    metadata?: Metadata;
    name:      string;
    reference: string;
    }

export interface Metadata {
    wbs?: string;
    }

export interface VarianceReason {
    end_date?:   EndDate;
    reason:      string;
    start_date?: StartDate;
    timestamp?:  string;
    }

export interface EndDate {
    is?:        string;
    timestamp?: string;
    was?:       string;
    }

export interface StartDate {
    is?:  string;
    was?: string;
    }

// Converts JSON strings to/from your types
// and asserts the results of JSON.parse at runtime
export class Convert {
    public static toActivitiesSchema(json: string): ActivitiesSchema {
        return cast(JSON.parse(json), r("ActivitiesSchema"));
    }

    public static activitiesSchemaToJson(value: ActivitiesSchema): string {
        return JSON.stringify(uncast(value, r("ActivitiesSchema")), null, 2);
    }
}

function invalidValue(typ: any, val: any, key: any, parent: any = ''): never {
    const prettyTyp = prettyTypeName(typ);
    const parentText = parent ? ` on ${parent}` : '';
    const keyText = key ? ` for key "${key}"` : '';
    throw Error(`Invalid value${keyText}${parentText}. Expected ${prettyTyp} but got ${JSON.stringify(val)}`);
}

function prettyTypeName(typ: any): string {
    if (Array.isArray(typ)) {
        if (typ.length === 2 && typ[0] === undefined) {
            return `an optional ${prettyTypeName(typ[1])}`;
        } else {
            return `one of [${typ.map(a => { return prettyTypeName(a); }).join(", ")}]`;
        }
    } else if (typeof typ === "object" && typ.literal !== undefined) {
        return typ.literal;
    } else {
        return typeof typ;
    }
}

function jsonToJSProps(typ: any): any {
    if (typ.jsonToJS === undefined) {
        const map: any = {};
        typ.props.forEach((p: any) => map[p.json] = { key: p.js, typ: p.typ });
        typ.jsonToJS = map;
    }
    return typ.jsonToJS;
}

function jsToJSONProps(typ: any): any {
    if (typ.jsToJSON === undefined) {
        const map: any = {};
        typ.props.forEach((p: any) => map[p.js] = { key: p.json, typ: p.typ });
        typ.jsToJSON = map;
    }
    return typ.jsToJSON;
}

function transform(val: any, typ: any, getProps: any, key: any = '', parent: any = ''): any {
    function transformPrimitive(typ: string, val: any): any {
        if (typeof typ === typeof val) return val;
        return invalidValue(typ, val, key, parent);
    }

    function transformUnion(typs: any[], val: any): any {
        // val must validate against one typ in typs
        const l = typs.length;
        for (let i = 0; i < l; i++) {
            const typ = typs[i];
            try {
                return transform(val, typ, getProps);
            } catch (_) {}
        }
        return invalidValue(typs, val, key, parent);
    }

    function transformEnum(cases: string[], val: any): any {
        if (cases.indexOf(val) !== -1) return val;
        return invalidValue(cases.map(a => { return l(a); }), val, key, parent);
    }

    function transformArray(typ: any, val: any): any {
        // val must be an array with no invalid elements
        if (!Array.isArray(val)) return invalidValue(l("array"), val, key, parent);
        return val.map(el => transform(el, typ, getProps));
    }

    function transformDate(val: any): any {
        if (val === null) {
            return null;
        }
        const d = new Date(val);
        if (isNaN(d.valueOf())) {
            return invalidValue(l("Date"), val, key, parent);
        }
        return d;
    }

    function transformObject(props: { [k: string]: any }, additional: any, val: any): any {
        if (val === null || typeof val !== "object" || Array.isArray(val)) {
            return invalidValue(l(ref || "object"), val, key, parent);
        }
        const result: any = {};
        Object.getOwnPropertyNames(props).forEach(key => {
            const prop = props[key];
            const v = Object.prototype.hasOwnProperty.call(val, key) ? val[key] : undefined;
            result[prop.key] = transform(v, prop.typ, getProps, key, ref);
        });
        Object.getOwnPropertyNames(val).forEach(key => {
            if (!Object.prototype.hasOwnProperty.call(props, key)) {
                result[key] = transform(val[key], additional, getProps, key, ref);
            }
        });
        return result;
    }

    if (typ === "any") return val;
    if (typ === null) {
        if (val === null) return val;
        return invalidValue(typ, val, key, parent);
    }
    if (typ === false) return invalidValue(typ, val, key, parent);
    let ref: any = undefined;
    while (typeof typ === "object" && typ.ref !== undefined) {
        ref = typ.ref;
        typ = typeMap[typ.ref];
    }
    if (Array.isArray(typ)) return transformEnum(typ, val);
    if (typeof typ === "object") {
        return typ.hasOwnProperty("unionMembers") ? transformUnion(typ.unionMembers, val)
            : typ.hasOwnProperty("arrayItems")    ? transformArray(typ.arrayItems, val)
            : typ.hasOwnProperty("props")         ? transformObject(getProps(typ), typ.additional, val)
            : invalidValue(typ, val, key, parent);
    }
    // Numbers can be parsed by Date but shouldn't be.
    if (typ === Date && typeof val !== "number") return transformDate(val);
    return transformPrimitive(typ, val);
}

function cast<T>(val: any, typ: any): T {
    return transform(val, typ, jsonToJSProps);
}

function uncast<T>(val: T, typ: any): any {
    return transform(val, typ, jsToJSONProps);
}

function l(typ: any) {
    return { literal: typ };
}

function a(typ: any) {
    return { arrayItems: typ };
}

function u(...typs: any[]) {
    return { unionMembers: typs };
}

function o(props: any[], additional: any) {
    return { props, additional };
}

function m(additional: any) {
    return { props: [], additional };
}

function r(name: string) {
    return { ref: name };
}

const typeMap: any = {
    "ActivitiesSchema": o([
        { json: "created_by", js: "created_by", typ: 0 },
        { json: "date_created", js: "date_created", typ: "" },
        { json: "date_modified", js: "date_modified", typ: "" },
        { json: "id", js: "id", typ: u(undefined, 0) },
        { json: "modified_by", js: "modified_by", typ: 0 },
        { json: "data", js: "data", typ: r("Data") },
    ], "any"),
    "Data": o([
        { json: "areas", js: "areas", typ: a(0) },
        { json: "blank_sticky_id", js: "blank_sticky_id", typ: u(undefined, 0) },
        { json: "comments", js: "comments", typ: a(0) },
        { json: "company", js: "company", typ: 0 },
        { json: "completed", js: "completed", typ: true },
        { json: "crew_size", js: "crew_size", typ: u(undefined, 0) },
        { json: "date_scanned", js: "date_scanned", typ: u(undefined, a("")) },
        { json: "description", js: "description", typ: u(undefined, "") },
        { json: "end_date", js: "end_date", typ: u(undefined, "") },
        { json: "estimated_duration", js: "estimated_duration", typ: 0 },
        { json: "mergedActivityNumbers", js: "mergedActivityNumbers", typ: u(undefined, 3.14) },
        { json: "number", js: "number", typ: 0 },
        { json: "origin", js: "origin", typ: u(undefined, r("Origin")) },
        { json: "predecessor", js: "predecessor", typ: u(undefined, a(u(0, ""))) },
        { json: "print_count", js: "print_count", typ: 3.14 },
        { json: "start_date", js: "start_date", typ: u(undefined, "") },
        { json: "tags", js: "tags", typ: a("") },
        { json: "title", js: "title", typ: "" },
        { json: "track_in_master_schedule", js: "track_in_master_schedule", typ: u(undefined, true) },
        { json: "variance_reasons", js: "variance_reasons", typ: u(undefined, a(r("VarianceReason"))) },
    ], "any"),
    "Origin": o([
        { json: "metadata", js: "metadata", typ: u(undefined, r("Metadata")) },
        { json: "name", js: "name", typ: "" },
        { json: "reference", js: "reference", typ: "" },
    ], "any"),
    "Metadata": o([
        { json: "wbs", js: "wbs", typ: u(undefined, "") },
    ], "any"),
    "VarianceReason": o([
        { json: "end_date", js: "end_date", typ: u(undefined, r("EndDate")) },
        { json: "reason", js: "reason", typ: "" },
        { json: "start_date", js: "start_date", typ: u(undefined, r("StartDate")) },
        { json: "timestamp", js: "timestamp", typ: u(undefined, "") },
    ], "any"),
    "EndDate": o([
        { json: "is", js: "is", typ: u(undefined, "") },
        { json: "timestamp", js: "timestamp", typ: u(undefined, "") },
        { json: "was", js: "was", typ: u(undefined, "") },
    ], "any"),
    "StartDate": o([
        { json: "is", js: "is", typ: u(undefined, "") },
        { json: "was", js: "was", typ: u(undefined, "") },
    ], "any"),
};
export type ActivitiesSchemaWithId = ActivitiesSchema & RequiredId
